import { ULID, ulid } from "ulid"

// Store the page instance ULID in a module-level variable, being a per-page-instance (not persistent across page loads).
var pageInstanceULID: ULID = ""

// Store the persistent device id, so that even if the user has not consented to persistent storage in their browser,
// it can still be consistent across a single page of events.
var globalDeviceId: string = ""

// The string used as a key in localStorage to store the persistent device id
const deviceIdId = "tcDeviceId"

// The ULID represents a single instance of a page being displayed to a user. It's generated once
// per page instance and shared across all events from that page instance.
export function getPageInstanceULID(): ULID {
  if (pageInstanceULID.length == 0) {
    pageInstanceULID = ulid()
  }
  return pageInstanceULID
}

// Try to determine if the current page is a preview in the draft editor or a PageBuilder page
// being edited.
export function pageIsDraft(): boolean {
  try {
    const knownDraftCssClasses = ["drafts", "drafts-edit"]
    if (knownDraftCssClasses.find((c) => document.body.classList.contains(c))) {
      return true
    }

    return (
      window.location.pathname.endsWith("/edit") || window.location.pathname.startsWith("/drafts/")
    )
  } catch {
    return false
  }
}

// Guess if the user's device is a mobile or desktop.
// 1024x600 is probably the smallest screen size we consider a desktop (e.g. 10" netbooks)
// Mobile phones report virtual "CSS Pixels", so even retina screen iPhones will report relatively
// small screen sizes. For e.g. iphone 15 pro max is 430 × 932.
// When checking the size, remember that a phone might be in landscape mode, so check height and width.
export function deviceType(): string {
  try {
    if (window.screen.width >= 600 && window.screen.height >= 600) return "desktop"
    else return "mobile"
  } catch {
    return "unknown"
  }
}

export function findOrCreateDeviceId(): string {
  globalDeviceId = window.localStorage?.getItem(deviceIdId) || globalDeviceId
  if (globalDeviceId == undefined || globalDeviceId.length < 26) {
    globalDeviceId = "v1:" + ulid()
  }
  return globalDeviceId
}

/**
 * Stores the device id in the browser.
 * Must only be called if one or more of these conditions is true:
 * - User has explicitly given GDPR-like consent.
 * - User is in a region where we have not enabled Didomi for consent management.
 * - The runtime environment has disabled Didomi globally.
 */
export function persistDeviceId() {
  try {
    window.localStorage?.setItem(deviceIdId, findOrCreateDeviceId())
  } catch (err) {
    // The two most likely causes of this error are:
    // a) The code is accidentally run server-side.
    // b) The client-side browser is too old or refusing access to localStorage

    // Ideally this would be sent to Bugsnag (client side) or the Nextjs logs (server side)
    // As the shared-components aren't across Bugsnag support yet, this is left as a TODO
    // and a log message. The log message is intended to reassure readers if they see it.

    console.log(
      "Unable to store internal analytics id for The Conversation. Core functionality will not be impacted.",
    )
  }
}

/**
 * Removes the persistent deviceId from browser storage.
 * Should be called if the user as revoked their GDPR-style consent.
 */
export function doNotPersistDeviceId() {
  try {
    window.localStorage?.removeItem(deviceIdId)
  } catch {
    // Ideally this would be sent to Bugsnag (client side) or the Nextjs logs (server side)
    // As the shared-components aren't across Bugsnag support yet, this is left as a TODO.
  }
}

// Only used in unit tests so far
export function resetGlobalDeviceId() {
  globalDeviceId = ""
}
